/**
 * PublicInductionForm - Contractor-facing induction form
 * Route: /i/:token/induction
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getBrief, parseRpcError } from '@shared/utils/publicRpc';
import { useInviteToken, buildInvitePath } from '@shared/utils/inviteToken';

export default function PublicInductionForm() {
  const token = useInviteToken();
  const navigate = useNavigate();
  const location = useLocation();
  const signatureCanvasRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [brief, setBrief] = useState(null);
  const [answers, setAnswers] = useState({});
  // signatureDataUrl — updated live from canvas; seeded from review state when editing
  const [signatureDataUrl, setSignatureDataUrl] = useState(
    location.state?.signatureDataUrl ?? null
  );
  const [returningFromReview] = useState(
    Boolean(location.state?.signatureDataUrl || location.state?.answers)
  );

  useEffect(() => {
    async function fetchBrief() {
      try {
        setLoading(true);
        setError(null);

        const data = await getBrief(token);
        
        if (!data) {
          setError('Unable to load induction brief. The invite may have expired.');
          return;
        }

        setBrief(data);
        
        // Initialize answers object based on brief content
        const initialAnswers = {};
        if (data.content && data.content.sections) {
          data.content.sections.forEach((section) => {
            // Skip info-only sections
            if (section.type === 'info') return;
            
            // Use section.id as the key
            const key = section.id;
            let defaultAnswer = '';
            
            if (section.type === 'checkbox') {
              defaultAnswer = false;
            } else if (section.type === 'radio' || section.type === 'select') {
              defaultAnswer = '';
            }
            
            initialAnswers[key] = {
              question: section.title,
              answer: defaultAnswer,
              type: section.type,
              required: section.required || false
            };
          });
        }
        // If returning from the review step, restore previously-entered answers
        // rather than blanking them out.
        if (location.state?.answers) {
          setAnswers(location.state.answers);
        } else {
          setAnswers(initialAnswers);
        }
      } catch (err) {
        setError(parseRpcError(err));
      } finally {
        setLoading(false);
      }
    }

    if (token) {
      fetchBrief();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Signature canvas handlers
  useEffect(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';

    let drawing = false;

    const startDrawing = (e) => {
      drawing = true;
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
      const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
      ctx.beginPath();
      ctx.moveTo(x, y);
    };

    const draw = (e) => {
      if (!drawing) return;
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
      const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const stopDrawing = () => {
      if (drawing) {
        drawing = false;
        setSignatureDataUrl(canvas.toDataURL('image/png'));
      }
    };

    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseleave', stopDrawing);
    canvas.addEventListener('touchstart', startDrawing, { passive: false });
    canvas.addEventListener('touchmove', draw, { passive: false });
    canvas.addEventListener('touchend', stopDrawing);

    return () => {
      canvas.removeEventListener('mousedown', startDrawing);
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mouseup', stopDrawing);
      canvas.removeEventListener('mouseleave', stopDrawing);
      canvas.removeEventListener('touchstart', startDrawing);
      canvas.removeEventListener('touchmove', draw);
      canvas.removeEventListener('touchend', stopDrawing);
    };
  }, [brief, loading]);

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureDataUrl(null);
    }
  };

  const handleAnswerChange = (key, value) => {
    setAnswers(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        answer: value
      }
    }));
  };

  const validateForm = () => {
    // Check all required answers are filled
    for (const key in answers) {
      const answer = answers[key];
      if (answer.type === 'checkbox' && answer.answer !== true) {
        return 'Please check all required boxes to proceed.';
      }
      if (answer.type === 'text' && !answer.answer.trim()) {
        return 'Please answer all questions.';
      }
    }

    // Check signature
    if (!signatureDataUrl) {
      return 'Please provide your signature before continuing.';
    }

    return null;
  };

  /**
   * Validate, then send the visitor to the review screen.
   * Actual submission happens only after they confirm there.
   */
  const handleGoToReview = (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      navigate(buildInvitePath(token, 'review'), {
        state: { answers, signatureDataUrl, brief },
      });
    } catch (err) {
      setError(parseRpcError(err));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading induction brief...</p>
        </div>
      </div>
    );
  }

  if (error && !brief) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Unable to Load Brief</h2>
          <p className="mt-2 text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!brief) {
    return null;
  }

  return (
    <form onSubmit={handleGoToReview} className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900">{brief.title}</h1>
        <p className="mt-2 text-sm text-gray-600">
          Please read each section carefully and complete all required fields.
        </p>
      </div>

      {/* Brief sections */}
      {brief.content && brief.content.sections && brief.content.sections.map((section, sectionIndex) => {
        const key = section.id;
        const answer = answers[key];
        
        return (
        <div key={section.id || sectionIndex} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {/* Info section */}
          {section.type === 'info' && (
            <>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{section.title}</h2>
              {section.content && <p className="text-sm text-gray-700">{section.content}</p>}
            </>
          )}

          {/* Checkbox field */}
          {section.type === 'checkbox' && (
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  id={key}
                  checked={answer?.answer === true}
                  onChange={(e) => handleAnswerChange(key, e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor={key} className="font-medium text-gray-700">
                  {section.label || section.title}
                  {section.required && <span className="text-red-500 ml-1">*</span>}
                </label>
              </div>
            </div>
          )}

          {/* Radio field */}
          {section.type === 'radio' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {section.title}
                {section.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <div className="space-y-2">
                {section.options && section.options.map((option, idx) => (
                  <div key={idx} className="flex items-center">
                    <input
                      type="radio"
                      id={`${key}_${idx}`}
                      name={key}
                      value={option}
                      checked={answer?.answer === option}
                      onChange={(e) => handleAnswerChange(key, e.target.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <label htmlFor={`${key}_${idx}`} className="ml-3 text-sm text-gray-700">
                      {option}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Textarea field */}
          {section.type === 'textarea' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {section.title}
                {section.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <textarea
                value={answer?.answer || ''}
                onChange={(e) => handleAnswerChange(key, e.target.value)}
                rows={4}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Your answer"
              />
            </div>
          )}

          {/* Select field */}
          {section.type === 'select' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {section.title}
                {section.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <select
                value={answer?.answer || ''}
                onChange={(e) => handleAnswerChange(key, e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select an option...</option>
                {section.options && section.options.map((option, idx) => (
                  <option key={idx} value={option}>{option}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        );
      })}

      {/* Signature section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Signature</h2>
        {returningFromReview && signatureDataUrl && (
          <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
            Your previous signature has been kept. Sign below to replace it, or leave the box blank to keep it.
          </div>
        )}
        <p className="text-sm text-gray-600 mb-4">
          Please sign below to confirm you have read and understood all sections of this induction.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Signature <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-gray-300 rounded-lg bg-white">
              <canvas
                ref={signatureCanvasRef}
                width={600}
                height={200}
                className="w-full touch-none"
                style={{ touchAction: 'none' }}
              />
            </div>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={clearSignature}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Clear Signature
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Submit button */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <button
          type="button"
          onClick={() => navigate(`/i/${token}`)}
          className="text-sm text-gray-600 hover:text-gray-900"
        >
          ← Back to invite
        </button>

        <button
          type="submit"
          className="inline-flex items-center gap-2 px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          Review &amp; Confirm
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </form>
  );
}
